// A record's path: its stages as a row of chevrons read left to right, as Salesforce draws a
// record's path - a counterparty's onboarding (TX-US-0042) and a deal's flow (TX-US-0043). A
// stage that opens something beneath the path is a link; which one is open is in the address, so
// a link and Back behave as tabs do. On a phone the path scrolls sideways with the current stage
// in view, scrolled within the path, never the page.
//
// Each stage is `{key, label, state, opens, title}`: `state` names its style (`path-${state}`)
// and is read out by `words`; `opens` says what choosing it opens, and is absent for a stage that
// opens nothing. `searchFor(key)` is the address with that stage open, or with none for null.

import { useEffect, useRef } from 'react';
import { Link } from 'react-router';

// The stage in view: where the record stands, or how it ended.
const IN_VIEW = ['current', 'warning', 'rejected'];
// The stage the record stands at.
const AT = ['current', 'warning'];

function Stage({ stage, open, words, searchFor }) {
  const said = `${stage.label}, ${words[stage.state]}`;
  const body = (
    <>
      {stage.state === 'done' && <span aria-hidden="true">✓</span>}
      {stage.label}
    </>
  );
  if (!stage.opens) {
    return <span className="path-stage-body" aria-label={said}>{body}</span>;
  }
  return (
    <Link className="path-stage-body" to={{ search: searchFor(open ? null : stage.key) }}
          aria-label={`${said}. ${open ? 'Close' : 'Open'} ${stage.opens}`}
          aria-expanded={open} title={stage.title ?? undefined}>
      {body}
    </Link>
  );
}

export default function PathStages({ stages, open, words, searchFor, scrollKey, label }) {
  const pathRef = useRef(null);
  useEffect(() => {
    const path = pathRef.current;
    const current = path?.querySelector('[data-current]');
    if (!path || !current) return;
    path.scrollLeft = current.offsetLeft - (path.clientWidth - current.offsetWidth) / 2;
  }, [scrollKey]);

  return (
    <ol className="onboarding-path" ref={pathRef} aria-label={label}>
      {stages.map((stage) => (
        <li key={stage.key} className={`path-stage path-${stage.state}`}
            data-current={IN_VIEW.includes(stage.state) || undefined}
            aria-current={AT.includes(stage.state) ? 'step' : undefined}>
          <Stage stage={stage} open={open === stage.key} words={words} searchFor={searchFor} />
        </li>
      ))}
    </ol>
  );
}
